import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import Popper from 'popper.js';

@Component({
    selector: 'pace-forms-dropdown-options',
    templateUrl: './dropdown-options.component.html',
    styleUrls: ['./dropdown-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownOptionsComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DropdownOptionsComponent),
            multi: true,
        },
    ],
})
export class DropdownOptionsComponent implements ControlValueAccessor, Validator, OnInit {
    @ViewChild('dropDownButton')
    public dropDownButton: ElementRef;

    @ViewChild('dropDownMenu')
    public dropDownMenu: ElementRef;

    @Input()
    public options: Array<{ key: string; value: string }>;

    public disabled = false;

    @Input()
    public mandatory = false;

    @Output()
    public valueChange: EventEmitter<unknown> = new EventEmitter<unknown>();

    public popperInstance: Popper | undefined;
    public faCaretDown = faCaretDown;
    public showDropdownMenu = false; // controls if the dropdown menu is visible
    private dropdownToggleTime = 0; // holds a timestamp used to debounce the dropdown toggle event
    public selectedOption: { key: string; value: string } | undefined;
    private control: FormControl;
    public constructor(private readonly cdr: ChangeDetectorRef) {}

    public ngOnInit(): void {
        this.control = new FormControl('');
    }

    public writeValue(value: string): void {
        this.selectedOption = this.options.find((option) => option.value === value);
        this.control.setValue(value);
    }

    public registerOnChange(fn: VoidFunction): void {
        if (fn && typeof fn === 'function') {
            this.valueChange.subscribe(fn);
        }
    }

    public registerOnTouched(): void {
        //
    }

    public validate(): { [key: string]: unknown } | null {
        const isInvalid = this.mandatory && !this.selectedOption;

        return isInvalid ? { required: true } : null;
    }

    public toggleDropdownFromMenu(event: UIEvent): boolean {
        event.preventDefault();

        if (typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
        }
        return !this.disabled && this.toggleDropdown();
    }

    public toggleDropdown(): boolean {
        // debounce event
        const now = Date.now();
        const dropdownDebounceTime = 250;
        if (now - this.dropdownToggleTime < dropdownDebounceTime) {
            return false;
        }

        // toggle dropdown
        this.dropdownToggleTime = now;
        this.showDropdownMenu = !this.showDropdownMenu;

        if (this.showDropdownMenu && this.dropDownButton && this.dropDownMenu) {
            this.popperInstance = new Popper(
                this.dropDownButton.nativeElement as HTMLElement,
                this.dropDownMenu.nativeElement as HTMLElement,
                {
                    placement: 'bottom-start',
                },
            );
            this.popperInstance.scheduleUpdate();
        } else if (typeof this.popperInstance !== 'undefined') {
            this.popperInstance.destroy();
            this.popperInstance = undefined;
        }

        return true;
    }

    public selectOption(option: { key: string; value: string }): void {
        this.selectedOption = option;
        this.control.setValue(option.value);
        this.valueChange.emit(option.key);
        this.showDropdownMenu = false;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.detectChanges();
    }
}
